import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { SortOrder } from "../hooks/usePaginatedKits";

type Column<T> = {
  header: string;
  className?: string;
  render: (item: T) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
};

interface KitsTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading: boolean;
  rowKey: (item: T, index: number) => string;
  emptyMessage: React.ReactNode;
  loadingMessage?: string;
  sortBy?: string | null;
  sortOrder?: SortOrder;
  onSort?: (key: string) => void;
}

/**
 * Reusable table wrapper for kit listings with shared empty/loading states.
 */
const KitsTable = <T,>({
  data,
  columns,
  loading,
  rowKey,
  emptyMessage,
  loadingMessage = "Loading results...",
  sortBy,
  sortOrder,
  onSort,
}: KitsTableProps<T>) => {
  return (
    <div className="table-wrapper">
      <table className="patient-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th 
                key={col.header} 
                className={`${col.className || ''} ${col.sortable ? 'sortable-header' : ''}`}
                onClick={() => col.sortable && col.sortKey && onSort?.(col.sortKey)}
                style={col.sortable ? { cursor: 'pointer' } : {}}
              >
                <div className="header-content-inner">
                  <span>{col.header}</span>
                  {col.sortable && col.sortKey && (() => {
                    const isActive = sortBy === col.sortKey;
                    let SortIcon: typeof ChevronUpIcon;
                    let iconClassName: string;
                    
                    if (isActive) {
                      SortIcon = sortOrder === 'asc' ? ChevronUpIcon : ChevronDownIcon;
                      iconClassName = "sort-icon active";
                    } else {
                      SortIcon = ChevronUpDownIcon;
                      iconClassName = "sort-icon";
                    }
                    
                    return (
                      <span className="sort-icon-container">
                        <SortIcon className={iconClassName} />
                      </span>
                    );
                  })()}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loading &&
            data.length > 0 &&
            data.map((item, index) => (
              <tr key={rowKey(item, index)} className="table-row">
                {columns.map((col) => (
                  <td key={col.header} className={col.className}>
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}

          {!loading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="no-results-cell">
                {emptyMessage}
              </td>
            </tr>
          )}

          {loading && (
            <tr>
              <td colSpan={columns.length} className="loading-cell">
                <div className="loading-content">
                  <div className="loading-spinner-small"></div>
                  <span>{loadingMessage}</span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default KitsTable;

